import {
    Card,
    CardContent,
    CardHeader,
    Heading,
    Icon,
    Icons,
    IconSizes,
    Typography,
} from '@toppy-webshop/toppy-design-system';
import React, {useEffect} from 'react';
import {
    RMA,
    SupportCase,
    useSupportCaseStore,
} from '../../store/supportCaseStore';
import CollapsibleProductLineList from '../organs/collapsibleProductLineList';
import {featureFlag} from '../../utils/featureFlag';
import ProgressBar from '../organs/progressBar';
import SupportCaseExplanation from '../cells/SolutionExplanation';
import {useAuthStore} from '../../store/authStore';
import {AuthError} from '../../errors/authError';

const geTitle = (supportCase: SupportCase) => {
    const solutionsTypes = [supportCase.requestedResolution];

    for (const item of supportCase.items) {
        solutionsTypes.push(item.requestedResolution);
    }

    solutionsTypes.filter(type => type !== null);

    return solutionsTypes
        .map(type => type?.label ?? null)
        .filter(label => label !== null)
        .filter((label, index, self) => self.indexOf(label) === index)
        .join(' + ');
};

interface SupportCaseListCardProps {
    supportCase: SupportCase;
    rma: RMA | null;
}

const SupportCaseListCard = ({supportCase, rma}: SupportCaseListCardProps) => {
    return (
        <Card key={supportCase.id}>
            <CardHeader>
                <Heading type={'h6'} noMargin>
                    {geTitle(supportCase)}
                </Heading>
            </CardHeader>
            <CardContent>
                {rma !== null && (
                    <ProgressBar
                        states={{
                            requested: {description: 'Aangevraagd'},
                            approved: {description: 'Goedgekeurd'},
                            received: {description: 'Ontvangen'},
                            processing: {description: 'Verwerken'},
                            completed: {description: 'Afgerond'},
                            closed: {description: 'Afgesloten'},
                        }}
                        currentState={rma.state}
                    />
                )}
                <SupportCaseExplanation supportCase={supportCase} />
            </CardContent>
            <CollapsibleProductLineList
                products={supportCase.items.map((item, index) => ({
                    key: index.toString(),
                    name: item.description,
                    qty: item.qty as number,
                }))}
            />
        </Card>
    );
};

const SupportCasesSection = () => {
    const {bearer, setInvalidBearer} = useAuthStore(state => state);
    const supportCases = useSupportCaseStore(state => state.supportCases);
    const getSupportCases = useSupportCaseStore(state => state.getSupportCases);

    useEffect(() => {
        if (null === bearer || null !== supportCases) {
            return;
        }

        const abortController = new AbortController();

        getSupportCases(bearer, abortController).catch((e: AuthError) => {
            setInvalidBearer(e.type);
        });

        return () => {
            abortController.abort();
        };
    }, [bearer, supportCases, getSupportCases, setInvalidBearer]);

    return featureFlag('zo_gefixt') ? (
        null !== supportCases && supportCases.length > 0 ? (
            <section>
                <Typography variant={'h5'} gutterBottom>
                    <Icon
                        icon={Icons.CHAT}
                        size={IconSizes.L}
                        color={'yellow'}
                    />
                    &nbsp; Vragen, problemen en retouren
                </Typography>
                {supportCases.map(item => (
                    <SupportCaseListCard
                        supportCase={item.supportCase}
                        rma={item.rma ?? null}
                        key={item.supportCase.id}
                    />
                ))}
            </section>
        ) : (
            <></>
        )
    ) : (
        <></>
    );
};

export default SupportCasesSection;
